import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Stack,
  Tab,
  Tabs,
  TableContainer,
  TablePagination,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';

const TONES = {
  success: { bg: '#e7f4ec', fg: '#1d7348' },
  warning: { bg: '#fdf2de', fg: '#9a5b06' },
  info: { bg: '#e6f0f7', fg: '#2c6489' },
  neutral: { bg: '#eef1f3', fg: '#5c6c72' },
  // Categories rather than states (e.g. an item's use): the brand teal and a quiet violet, so
  // they never read as done (green) or needs attention (amber).
  brand: { bg: '#e8f3f0', fg: '#17665f' },
  violet: { bg: '#efecf7', fg: '#5a4b8c' },
} as const;
export type Tone = keyof typeof TONES;
/** The tones that mean a state: done, needs attention, in progress, idle. */
export type StatusTone = Exclude<Tone, 'brand' | 'violet'>;

/**
 * A read-only state label: soft fill and a leading dot, no border, so it never looks like
 * the outlined or filled buttons that sit next to it.
 */
export function Tag({ tone = 'neutral', children }: { tone?: Tone; children: React.ReactNode }) {
  const { bg, fg } = TONES[tone];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.375,
        borderRadius: 1,
        bgcolor: bg,
        color: fg,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        cursor: 'default',
        userSelect: 'none',
      }}
    >
      <Box
        component="span"
        sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', flex: 'none' }}
      />
      {children}
    </Box>
  );
}

export function Empty({ children = '暂时没有相关记录' }: { children?: React.ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Heading({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        <Typography
          variant="h4"
          sx={{
            fontSize: 28,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            color="text.secondary"
            sx={{
              fontSize: 13,
              mt: 1,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </div>
      <Stack direction="row" spacing={1}>
        {action}
      </Stack>
    </div>
  );
}

/**
 * Top bar of a list card: filter tabs on the left, optional tools (search, export) on the right.
 * Every filterable list uses this so filters look and behave the same across pages.
 */
export function ListToolbar(p: { children: ReactNode }) {
  return (
    <Box
      sx={{
        px: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        borderBottom: '1px solid #e7edef',
      }}
    >
      {p.children}
    </Box>
  );
}

export interface CountTab<T extends string> {
  value: T;
  label: string;
  count: number;
  /** Highlights a non-zero count: amber for work waiting on us, 'error' (red) for overdue. */
  alert?: boolean | 'error';
}

/** Filter tabs with a count badge on each, as used by the order and item lists. */
export function CountTabs<T extends string>(p: {
  value: T;
  onChange: (value: T) => void;
  items: CountTab<T>[];
}) {
  return (
    <Tabs
      value={p.value}
      onChange={(_, v) => p.onChange(v)}
      // Fill the toolbar's height so the selected underline sits on its bottom edge, even when
      // taller tools (a search box) share the bar.
      sx={{
        alignSelf: 'stretch',
        '& .MuiTabs-scroller, & .MuiTabs-list': { height: '100%' },
      }}
    >
      {p.items.map((item) => {
        const loud = item.alert && item.count > 0;
        return (
          <Tab
            key={item.value}
            value={item.value}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {item.label}
                <Box
                  component="span"
                  sx={{
                    minWidth: 20,
                    px: 0.75,
                    borderRadius: 999,
                    fontSize: 11,
                    lineHeight: '18px',
                    textAlign: 'center',
                    // Work waiting on us stands out; other counts stay quiet.
                    bgcolor: loud ? (item.alert === 'error' ? 'error.main' : '#e09a2b') : '#eef1f3',
                    color: loud ? '#fff' : 'text.secondary',
                  }}
                >
                  {item.count}
                </Box>
              </Box>
            }
          />
        );
      })}
    </Tabs>
  );
}

/**
 * A table container that never pushes what follows it (the pager, the page's bottom padding)
 * below the screen: it measures the room between its own top and whatever sits under it, caps
 * its height to fit, and scrolls inside. Pair it with `<Table stickyHeader>` so the column
 * headers stay put. Measured, not a fixed offset, so every page's heading and toolbar fit.
 */
export function FitTableContainer(p: { children: ReactNode; sx?: SxProps<Theme>; min?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();
  const min = p.min ?? 320;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      const box = el.getBoundingClientRect();
      const top = box.top + window.scrollY;
      // Everything laid out below the table: the pager row, card edge and page padding.
      const below = document.body.offsetHeight - (box.bottom + window.scrollY);
      const next = Math.max(min, Math.floor(window.innerHeight - top - below));
      setMaxHeight((prev) => (prev === next ? prev : next));
    };
    fit();
    // Re-fit on window resize and whenever the page around it changes size (toolbar wrapping,
    // a pager appearing); the cap itself doesn't change `below`, so this settles at once.
    const observer = new ResizeObserver(fit);
    observer.observe(document.body);
    window.addEventListener('resize', fit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [min]);
  return (
    <TableContainer ref={ref} sx={[{ maxHeight }, ...(Array.isArray(p.sx) ? p.sx : [p.sx])]}>
      {p.children}
    </TableContainer>
  );
}

const PAGE_SIZES = [10, 20, 50];

/**
 * Client-side paging for a loaded list, with MUI's TablePagination. Returns to the first page
 * whenever `resetKey` changes (a new tab or search), and shows no pager for a single short page.
 */
export function usePagination<T>(items: T[], resetKey: string) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  useEffect(() => setPage(0), [resetKey]);
  // Stay on a real page when the list shrinks (e.g. an order moved to another tab).
  const current = Math.min(page, Math.max(0, Math.ceil(items.length / size) - 1));
  const pageItems = items.slice(current * size, current * size + size);
  const pager =
    items.length > PAGE_SIZES[0] ? (
      <TablePagination
        component="div"
        count={items.length}
        page={current}
        rowsPerPage={size}
        rowsPerPageOptions={PAGE_SIZES}
        onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={(e) => {
          setSize(Number(e.target.value));
          setPage(0);
        }}
        labelRowsPerPage="每页"
        getItemAriaLabel={(type) =>
          ({ first: '第一页', last: '最后一页', next: '下一页', previous: '上一页' })[type]
        }
        labelDisplayedRows={({ from, to, count }) => `${from}–${to}，共 ${count} 条`}
      />
    ) : null;
  return { pageItems, pager };
}
